import React, { useEffect, useRef, useState } from "react";
import { Alert, Spin, Table, Tabs } from "antd";
import {
  buildExcelPreviewTableColumns,
  buildExcelPreviewTableData,
  fetchProjectDocumentationFileBlob,
  parseExcelBlobForPreview,
} from "../../../app/utils/common/projectDocumentationFile";
import "./ProjectDocumentationExcelViewer.scss";

const TRUNCATION_NOTE =
  "Showing the first 500 rows and 40 columns. Download the file for the full sheet.";

const SheetTable = ({ sheet, compact }) => {
  if (!sheet?.rows?.length) {
    return <p className="text-gray-500 text-sm m-0">This sheet is empty.</p>;
  }

  const columns = buildExcelPreviewTableColumns(sheet.colCount);
  const dataSource = buildExcelPreviewTableData(sheet.rows);

  return (
    <div className="project-doc-excel-viewer__sheet">
      {sheet.truncated ? <p className="project-doc-excel-viewer__note">{TRUNCATION_NOTE}</p> : null}
      <div className="project-doc-excel-viewer__table-wrap">
        <Table
          size="small"
          columns={columns}
          dataSource={dataSource}
          pagination={{ pageSize: compact ? 15 : 25, showSizeChanger: !compact }}
          scroll={{ x: "max-content", y: compact ? 280 : 480 }}
        />
      </div>
    </div>
  );
};

/**
 * Read-only Excel preview for project documentation (fetches via API when documentId is set).
 */
const ProjectDocumentationExcelViewer = ({
  documentId,
  fileDocumentId,
  blob,
  onLoadError,
  compact = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const onLoadErrorRef = useRef(onLoadError);
  onLoadErrorRef.current = onLoadError;

  useEffect(() => {
    let cancelled = false;

    const fail = (err) => {
      if (cancelled) return;
      setLoadError(err?.message || "Could not load the Excel file.");
      setSheets([]);
      setActiveSheet(null);
      setLoading(false);
      if (typeof onLoadErrorRef.current === "function") onLoadErrorRef.current(err);
    };

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      setSheets([]);
      setActiveSheet(null);
      try {
        let fileBlob = blob;
        if (!fileBlob && documentId) {
          fileBlob = await fetchProjectDocumentationFileBlob(documentId, fileDocumentId);
        }
        if (!fileBlob) {
          throw new Error("No Excel file to preview.");
        }
        const parsed = await parseExcelBlobForPreview(fileBlob);
        if (cancelled) return;
        if (!parsed.length) {
          setLoadError("This workbook has no sheets to display.");
          setLoading(false);
          return;
        }
        setSheets(parsed);
        setActiveSheet(parsed[0].sheetName);
      } catch (err) {
        fail(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [documentId, fileDocumentId, blob]);

  if (loading) {
    return (
      <div className={`project-doc-excel-viewer ${compact ? "project-doc-excel-viewer--compact" : ""}`}>
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={`project-doc-excel-viewer ${compact ? "project-doc-excel-viewer--compact" : ""}`}>
        <Alert type="error" showIcon message={loadError} />
      </div>
    );
  }

  const active = sheets.find((s) => s.sheetName === activeSheet) || sheets[0];

  return (
    <div
      className={`project-doc-excel-viewer ${compact ? "project-doc-excel-viewer--compact" : ""}`}
      onDragStart={(e) => e.preventDefault()}
    >
      {sheets.length > 1 ? (
        <Tabs
          activeKey={activeSheet || sheets[0].sheetName}
          onChange={setActiveSheet}
          items={sheets.map((sheet) => ({
            key: sheet.sheetName,
            label: sheet.sheetName,
            children: <SheetTable sheet={sheet} compact={compact} />,
          }))}
          className="project-doc-excel-viewer__tabs"
        />
      ) : (
        <SheetTable sheet={active} compact={compact} />
      )}
    </div>
  );
};

export default ProjectDocumentationExcelViewer;
